import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { SplashAuthShell } from './SplashAuthShell';
import { formatPhone } from '../lib/format';

// Open signup (Casey 11/3/26): no PIN. Everyone gets the same path; documents, insurance and a
// checkout are the gate. The lgb61.com popup sends people here with their details after a '#'
// (never the query string, so they stay out of server logs): #fn=&ln=&email=&phone=&kind=&cert=&hours=&ins=&instr=
type Intake = { kind?: string; cert?: string; hours?: string; insurance?: string; instructor?: string; source?: string };

function readPrefill() {
  try {
    const h = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const g = (k: string, n = 120) => (h.get(k) || '').trim().slice(0, n);
    const intake: Intake = {};
    if (g('kind')) intake.kind = g('kind', 60);
    if (g('cert')) intake.cert = g('cert', 40);
    if (g('hours')) intake.hours = g('hours', 10);
    if (g('ins')) intake.insurance = g('ins', 20);
    if (g('instr')) intake.instructor = g('instr', 80);
    if (Object.keys(intake).length) intake.source = 'lgb61-popup';
    return { fn: g('fn', 60), ln: g('ln', 60), email: g('email', 120), phone: g('phone', 30), intake };
  } catch {
    return { fn: '', ln: '', email: '', phone: '', intake: {} as Intake };
  }
}

export function Register() {
  const { signUpLessee, configured } = useAuth();
  const nav = useNavigate();
  const [pre] = useState(readPrefill);
  const fromSite = !!pre.intake.source;

  const [firstName, setFirstName] = useState(pre.fn);
  const [lastName, setLastName] = useState(pre.ln);
  const [email, setEmail] = useState(pre.email);
  const [phone, setPhone] = useState(pre.phone ? formatPhone(pre.phone) : '');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [formErr, setFormErr] = useState<string | null>(null);
  const [formBusy, setFormBusy] = useState(false);

  function passwordOk(p: string) {
    return p.length >= 10 && /[A-Za-z]/.test(p) && /\d/.test(p);
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormErr(null);
    if (!firstName.trim() || !lastName.trim()) return setFormErr('Enter your full legal name.');
    if (!phone.trim()) return setFormErr('Enter your phone number.');
    if (!passwordOk(password)) {
      return setFormErr('Password must be at least 10 characters with letters and a number.');
    }
    if (password !== password2) return setFormErr('Passwords do not match.');

    setFormBusy(true);
    try {
      await signUpLessee({ email, password, firstName, lastName, phone, intake: fromSite ? pre.intake : undefined });
      // Drop the prefill from the address bar once it's used.
      try { window.history.replaceState(null, '', window.location.pathname); } catch { /* ignore */ }
      nav('/pending', { replace: true });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Could not create account.';
      setFormErr(msg.replace('Firebase: ', ''));
    } finally {
      setFormBusy(false);
    }
  }

  return (
    <SplashAuthShell
      title={fromSite ? 'Set your password' : 'Create your account'}
      subtitle={
        fromSite
          ? 'Last step to open your renter account. Then the app walks you through your documents.'
          : 'Every renter uploads their documents and insurance. We review them before you can book.'
      }
    >
      {!configured && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <strong className="font-semibold">Setup pending.</strong> Firebase credentials haven’t been added yet. See <code>SETUP.md</code>.
        </div>
      )}
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label" htmlFor="fn">First name</label>
              <input id="fn" className="input" required value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </div>
            <div>
              <label className="label" htmlFor="ln">Last name</label>
              <input id="ln" className="input" required value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </div>
          </div>
          <div>
            <label className="label" htmlFor="em">Email</label>
            <input id="em" type="email" autoComplete="email" required className="input" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div>
            <label className="label" htmlFor="ph">Phone</label>
            <input id="ph" type="tel" autoComplete="tel" required className="input" value={phone} onChange={(e) => setPhone(formatPhone(e.target.value))} />
          </div>
          <div>
            <label className="label" htmlFor="pw">Password</label>
            <input id="pw" type="password" autoComplete="new-password" required className="input" value={password} onChange={(e) => setPassword(e.target.value)} />
            <p className="text-xs text-ink-muted mt-1">At least 10 characters, letters and a number.</p>
          </div>
          <div>
            <label className="label" htmlFor="pw2">Confirm password</label>
            <input id="pw2" type="password" autoComplete="new-password" required className="input" value={password2} onChange={(e) => setPassword2(e.target.value)} />
          </div>
          {formErr && (
            <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              {formErr}
            </div>
          )}
          <button type="submit" disabled={formBusy || !configured} className="btn-primary w-full">
            {formBusy ? 'Creating account…' : 'Create account'}
          </button>
          <div className="text-sm text-center">
            <Link to="/login" className="text-navy-700 hover:text-navy-900 underline">
              Already have an account? Sign in
            </Link>
          </div>
        </form>
    </SplashAuthShell>
  );
}
